import useHrStore from '@/modules/people/store/hr';
import useHrUpgradeStore from '@apps-hr/modules/upgrade/store';
import useWebappStore from '@webapp-next/store';
import checkFF from '@webapp-next/lib/feature-flag';
import userHasPermission from '@webapp-next/lib/user-has-permission';
import { ddLogError } from '@webapp-next/lib/utils/dd-log';
import { fetchV2SubordinateCheck } from '@/modules/people/lib/request/fetch-v2-subordinate-check';
import Employee from '@/modules/people/types/employee';
import { ROLES } from '../definitions';

/**
 * Returns whether the Team Performance tab should be available for the
 * currently viewed employee profile.
 *
 * Access rules (all must be true):
 *  - The team performance feature flag is enabled
 *  - The viewer has Can_View_Team_Performance or Can_Manage_Team_Performance
 *  - The viewer is NOT looking at their own profile
 *  - The viewed employee is NOT a System Administrator
 *  - The viewed employee is a subordinate of the viewer
 *
 * Infrastructure rules (applied after access rules):
 *  - If HR is paywalled, return true so the tab renders as an upsell
 *  - Otherwise, defer to the org-level `isTeamPerformanceEnabled` setting
 */
export default async function canAccessTeamPerformanceTab(
  employee: Partial<Employee>
): Promise<boolean> {
  if (!checkFF('chip-45-team-performance')) {
    return false;
  }

  const hasTeamPerformancePermission =
    userHasPermission('Can_View_Team_Performance') ||
    userHasPermission('Can_Manage_Team_Performance');
  if (!hasTeamPerformancePermission) {
    return false;
  }

  const webappStore = useWebappStore();

  // webappStore.getUserId() returns the Employee ID of the currently logged-in user
  const isOwnProfile = employee.Id === webappStore.getUserId();
  if (isOwnProfile) {
    return false;
  }

  const isViewingAdmin = employee.Role === ROLES.SYSTEM_ADMINISTRATOR;
  if (isViewingAdmin) {
    return false;
  }

  const viewedEmployeeId = employee.Id;
  if (!viewedEmployeeId) {
    return false;
  }

  let isSubordinate = false;
  try {
    const result = await fetchV2SubordinateCheck(viewedEmployeeId);
    isSubordinate = result.isSubordinate;
  } catch (error) {
    // On API failure, deny access — the tab is hidden rather than risk
    // exposing team-performance data to a non-manager. Log so we can
    // detect endpoint regressions in production.
    ddLogError('Subordinate-check failed', {
      context: 'can-access-team-performance-tab',
      metadata: { error, viewedEmployeeId },
    });
    return false;
  }
  if (!isSubordinate) {
    return false;
  }

  const hrUpgradeStore = useHrUpgradeStore();
  if (hrUpgradeStore.isHrBlocked()) {
    return true;
  }

  const hrStore = useHrStore();
  return hrStore.isTeamPerformanceEnabled;
}
